import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { Box, Stack } from '@mui/material';
import { CheckboxGroup, EnumDropdown, ModalFormButton } from 'framework/form';
import { Alert, Tooltip } from 'framework/ui/mui-builder';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import {
  RECRUITING_VACANCY_GROUP_UPDATE,
  VACANCY_POSITION_SLOT_WORKER_TYPE_OPTIONS,
  useVacancyGroupService,
} from 'domain/shared/newVacancy';
import type { VacancyGroupPositionSlotData, VacancyGroupType } from 'domain/shared/newVacancy';
import { POSITION_SLOT_API_OFFICE_OPTIONS, POSITION_SLOT_OFFICES_CHECKBOX_SX } from 'domain/shared/employee/office';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import vacancyGroupPositionSlotDataValidationSchema from './vacancyGroupPositionSlotDataValidationSchema';

const WARNING_MESSAGE = 'Position slot worker type or office is not specified.';
const POSITION_SLOT_DATA_FORM_TITLE = 'Add position slot data';

const VacancyGroupPositionSlotWarning = () => {
  const { isGranted } = useAuthorization();
  const { data, setData } = useShowContext<VacancyGroupType>();
  const showData = data.getData();
  const { updateVacancyGroupPositionSlotData } = useVacancyGroupService();

  const isPositionSlotWorkerTypeMissing = !showData?.positionSlotWorkerType;
  const isPositionSlotOfficesMissing = !showData?.positionSlotOffices?.length;
  const isPositionSlotWarningVisible = isPositionSlotWorkerTypeMissing || isPositionSlotOfficesMissing;

  if (!showData || !isPositionSlotWarningVisible) {
    return null;
  }

  const canUpdate = isGranted(RECRUITING_VACANCY_GROUP_UPDATE, RECRUITING_CONTEXT);

  return (
    <Box display="flex" justifyContent="flex-end" mt={2}>
      <Alert severity="warning" sx={{ width: 'fit-content', alignItems: 'center' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {WARNING_MESSAGE}
          {canUpdate && (
            <ModalFormButton
              label={
                <Tooltip title={POSITION_SLOT_DATA_FORM_TITLE}>
                  <AppRegistrationIcon color="primary" sx={{ fontSize: 20 }} />
                </Tooltip>
              }
              formTitle={POSITION_SLOT_DATA_FORM_TITLE}
              getValidationSchemaFunc={() => Promise.resolve(vacancyGroupPositionSlotDataValidationSchema)}
              submitFormFunc={values =>
                updateVacancyGroupPositionSlotData(showData.id, values as VacancyGroupPositionSlotData)
              }
              initialData={{
                positionSlotOffices: showData.positionSlotOffices || [],
                positionSlotWorkerType: showData.positionSlotWorkerType || null,
              }}
              afterSubmit={{
                execute: response => {
                  setData?.({
                    ...showData,
                    ...response,
                  });
                },
                message: 'Position slot data has been successfully updated.',
              }}
              color="transparent"
              buttonProps={{
                'aria-label': POSITION_SLOT_DATA_FORM_TITLE,
              }}
              sxButton={{ minWidth: 'auto', p: 0 }}
            >
              <CheckboxGroup
                source="positionSlotOffices"
                label="Location"
                options={POSITION_SLOT_API_OFFICE_OPTIONS}
                sx={POSITION_SLOT_OFFICES_CHECKBOX_SX}
              />
              <EnumDropdown
                source="positionSlotWorkerType"
                label="Category"
                options={VACANCY_POSITION_SLOT_WORKER_TYPE_OPTIONS}
              />
            </ModalFormButton>
          )}
        </Stack>
      </Alert>
    </Box>
  );
};

export default VacancyGroupPositionSlotWarning;
